/**
 * Persera Telemetry - Browser SDK
 *
 * Zero-dependency client for funnel events and client-side errors. Copy this single file into
 * any app's client. The hub identifies the app from the page's Origin, so no key is needed.
 *
 *   telemetry.init({
 *       consent: () => localStorage.getItem('cookie') === 'accepted',
 *   });
 *   telemetry.identify(user?.guid);           // links this visitor to a user for funnels
 *   telemetry.page();                         // "page_view" with { path }
 *   telemetry.track('checkout_started', { plan: 'year' });
 *
 * Events are queued and sent every few seconds, and with navigator.sendBeacon when the
 * page is hidden, so nothing is lost on navigation. Never put emails, names or free text in props.
 *
 * GDPR: nothing is collected, sent or stored until `consent()` returns true (opt-in). Withdrawing
 * consent drops anything queued and deletes the stored visitor id.
 */

type PropValue = string | number | boolean | null;

interface QueuedEvent {
    name: string;
    ts: number;
    vid: string;
    uid?: string;
    props?: Record<string, PropValue>;
}

interface QueuedLog {
    level: 'info' | 'warn' | 'error';
    msg: string;
    ts: number;
    ctx?: Record<string, unknown>;
}

export interface BrowserTelemetryOptions {
    endpoint?: string;
    flushIntervalMs?: number;
    /** Opt-in check, read on every call (e.g. the performance-cookie choice). Default: no consent. */
    consent?: () => boolean;
    /** Report uncaught errors and unhandled rejections as error logs. Default: true */
    captureErrors?: boolean;
    disabled?: boolean;
}

const VID_KEY = 'persera_vid';
const MAX_QUEUE = 200;
const MAX_ERRORS_PER_PAGE = 10;
const MAX_BEACON_ITEMS = 50; // hub limits per browser batch
const MAX_BEACON_LOGS = 20;

/**
 * The hub for the domain this page is served from: apps on *.persera.ca report to www.persera.ca, apps on
 * *.dsolver.ca (staging) to www.dsolver.ca, and localhost to the hub's local dev server.
 */
export function defaultHubEndpoint(): string {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:4444/api/telemetry/ingest';
    return `https://www.${host.split('.').slice(-2).join('.')}/api/telemetry/ingest`;
}

const ID_SEGMENT = /^([0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

/** "/c/acme/profiles/65f0c2..." -> "/c/acme/profiles/:id", so steps match and person ids stay out of events. */
export function normalizePath(path: string): string {
    return path
        .split('/')
        .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
        .join('/');
}

function randomId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return Array.from({ length: 4 }, () => Math.random().toString(16).slice(2, 10).padEnd(8, '0')).join('-');
}

class BrowserTelemetry {
    private endpoint = '';
    private consent: () => boolean = () => false;
    private disabled = true; // until init()
    private memoryVid = randomId();
    private uid?: string;
    private events: QueuedEvent[] = [];
    private logs: QueuedLog[] = [];
    private errorsReported = 0;

    init(options: BrowserTelemetryOptions = {}) {
        if (typeof window === 'undefined') return;
        this.endpoint = options.endpoint ?? defaultHubEndpoint();
        this.consent = options.consent ?? this.consent;
        this.disabled = !!options.disabled;
        if (this.disabled) return;

        setInterval(() => this.flush(), options.flushIntervalMs ?? 5_000);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') this.flush();
        });
        window.addEventListener('pagehide', () => this.flush());

        if (options.captureErrors !== false) {
            window.addEventListener('error', (e) => this.captureError(e.message || 'Uncaught error', { source: e.filename, line: e.lineno }));
            window.addEventListener('unhandledrejection', (e) =>
                this.captureError(`Unhandled rejection: ${e.reason?.message ?? String(e.reason)}`, {})
            );
        }
    }

    /** Set (or clear) the signed-in user's guid. */
    identify(uid?: string | null) {
        this.uid = uid || undefined;
    }

    track(name: string, props?: Record<string, PropValue>) {
        if (!this.allowed() || this.events.length >= MAX_QUEUE) return;
        this.events.push({ name, ts: Date.now(), vid: this.visitorId(), ...(this.uid ? { uid: this.uid } : {}), ...(props ? { props } : {}) });
    }

    page(path: string = window.location.pathname) {
        this.track('page_view', { path: normalizePath(path) });
    }

    warn(msg: string, ctx?: Record<string, unknown>) {
        this.log('warn', msg, ctx);
    }

    error(msg: string, ctx?: Record<string, unknown>) {
        this.log('error', msg, ctx);
    }

    flush() {
        if (!this.allowed()) return;
        while (this.events.length || this.logs.length) {
            const events = this.events.splice(0, MAX_BEACON_ITEMS);
            const logs = this.logs.splice(0, MAX_BEACON_LOGS);
            this.send(JSON.stringify({ events, logs }));
        }
    }

    private log(level: QueuedLog['level'], msg: string, ctx?: Record<string, unknown>) {
        if (!this.allowed() || this.logs.length >= MAX_QUEUE) return;
        this.logs.push({ level, msg: String(msg).slice(0, 2000), ts: Date.now(), ctx: { path: window.location.pathname, ...ctx } });
    }

    private captureError(msg: string, ctx: Record<string, unknown>) {
        if (this.errorsReported++ >= MAX_ERRORS_PER_PAGE) return;
        this.log('error', msg, ctx);
    }

    private send(body: string) {
        try {
            // text/plain keeps this a "simple" request: no CORS preflight
            const blob = new Blob([body], { type: 'text/plain' });
            if (navigator.sendBeacon?.(this.endpoint, blob)) return;
            void fetch(this.endpoint, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
        } catch {
            // telemetry must never break the page
        }
    }

    /** Consent is re-read on every call so a withdrawal takes effect immediately. */
    private allowed(): boolean {
        if (this.disabled) return false;
        let granted = false;
        try {
            granted = this.consent();
        } catch {
            granted = false;
        }
        if (!granted) {
            this.events = [];
            this.logs = [];
            try {
                localStorage.removeItem(VID_KEY);
            } catch {
                // storage unavailable
            }
        }
        return granted;
    }

    private visitorId(): string {
        try {
            let vid = localStorage.getItem(VID_KEY);
            if (!vid) {
                vid = this.memoryVid;
                localStorage.setItem(VID_KEY, vid);
            }
            return vid;
        } catch {
            return this.memoryVid;
        }
    }
}

export const telemetry = new BrowserTelemetry();
